// frontend/src/components/EmojiTranslator.js
//
// The tool behind /tools/emoji-translator. Client-side, no endpoint.
//
// THE READOUT IS THE POINT. Converting words to emoji is a two-hour build and
// five sites already do it. What none of them show is how the result is HEARD:
// a screen reader announces every emoji aloud by its name, so "Big news 🔥🔥🔥"
// arrives as "Big news fire fire fire". RNIB and Scope for Business both give
// specific, quotable guidance — no more than three, placed at the END of a
// caption, never carrying meaning on their own — and this component measures
// against exactly that.
import React, { useMemo, useState } from 'react'
import { Button, Form, Row, Col, ButtonGroup, Badge } from 'react-bootstrap'
import { Clipboard, CheckLg, ArrowLeftRight, VolumeUpFill } from 'react-bootstrap-icons'

import { toEmoji, toText, speak, emojiReport, EMOJI_LIST } from '../utils/emojiText'

const SAMPLE = 'Big news — our new bakery opens today. Coffee and cake on the house, and thanks for all the support.'

export default function EmojiTranslator () {
  const [input, setInput] = useState(SAMPLE)
  const [dir, setDir] = useState('to-emoji')   // 'to-emoji' | 'to-text'
  const [mode, setMode] = useState('add')      // 'add' | 'replace'
  const [copied, setCopied] = useState(false)

  const output = useMemo(
    () => (dir === 'to-emoji' ? toEmoji(input, mode) : toText(input)),
    [input, dir, mode],
  )
  const readout = useMemo(() => speak(output), [output])
  const report = useMemo(() => emojiReport(output), [output])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked — the output is selectable */ }
  }

  const swap = () => {
    setInput(output)
    setDir(d => (d === 'to-emoji' ? 'to-text' : 'to-emoji'))
  }

  const variant = { none: 'secondary', ok: 'success', warn: 'warning', bad: 'danger' }[report.level]
  const verdict = {
    none: 'No emoji. Nothing for a screen reader to announce.',
    ok: 'Within the guidance — three or fewer, and none interrupting a sentence.',
    warn: `${report.inline} emoji sit inside the text rather than at the end, so they interrupt the sentence when read aloud.`,
    bad: `${report.count} emoji. RNIB and Scope both advise no more than three — beyond that the caption becomes tiring to listen to.`,
  }[report.level]

  return (
    <div className='p-4 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
      <div className='d-flex flex-wrap gap-3 align-items-center mb-3'>
        <ButtonGroup size='sm'>
          <Button variant={dir === 'to-emoji' ? 'primary' : 'outline-primary'}
                  onClick={() => setDir('to-emoji')}>Text to emoji</Button>
          <Button variant={dir === 'to-text' ? 'primary' : 'outline-primary'}
                  onClick={() => setDir('to-text')}>Emoji to text</Button>
        </ButtonGroup>

        {dir === 'to-emoji' && (
          <ButtonGroup size='sm'>
            <Button variant={mode === 'add' ? 'secondary' : 'outline-secondary'}
                    onClick={() => setMode('add')}>Keep the words</Button>
            <Button variant={mode === 'replace' ? 'secondary' : 'outline-secondary'}
                    onClick={() => setMode('replace')}>Replace the words</Button>
          </ButtonGroup>
        )}

        <Button size='sm' variant='outline-secondary' onClick={swap} title='Send the result back to the input'>
          <ArrowLeftRight className='me-1' />Swap
        </Button>
      </div>

      <Row className='g-3'>
        <Col md={6}>
          <Form.Label className='fw-semibold small'>
            {dir === 'to-emoji' ? 'Your text' : 'Text containing emoji'}
          </Form.Label>
          <Form.Control as='textarea' rows={6} value={input}
                        onChange={e => setInput(e.target.value)}
                        style={{ fontSize: '1rem', lineHeight: 1.6 }} />
        </Col>
        <Col md={6}>
          <Form.Label className='fw-semibold small'>Result</Form.Label>
          <div className='p-3 rounded-3 h-100' style={{ border: '1px solid #e5e7eb', background: '#f8f9fa', minHeight: 148 }}>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '1rem' }}>{output}</div>
          </div>
        </Col>
      </Row>

      <div className='mt-3'>
        <Button size='sm' variant='primary' onClick={copy}>
          {copied ? <><CheckLg className='me-1' />Copied</> : <><Clipboard className='me-1' />Copy result</>}
        </Button>
      </div>

      <hr className='my-4' />

      {/* ── the readout: what nobody else shows ── */}
      <Row className='g-4'>
        <Col lg={7}>
          <h3 className='h6 fw-bold mb-2'>
            <VolumeUpFill className='me-2 text-primary' />How a screen reader reads this
          </h3>
          <div className='p-3 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
            <p className='mb-0' style={{ lineHeight: 1.7, fontStyle: 'italic' }}>
              “{readout || 'Nothing to read.'}”
            </p>
          </div>
          <p className='text-muted small mt-2 mb-0'>
            Every emoji is announced aloud by its name, in the position it sits. Names come from
            the Unicode CLDR list, which is what assistive software actually reads — and they do
            not always match what you meant by the picture.
          </p>
        </Col>

        <Col lg={5}>
          <h3 className='h6 fw-bold mb-2'>Accessibility check</h3>
          <div className='p-3 rounded-3' style={{ border: '1px solid #e5e7eb' }}>
            <div className='d-flex gap-2 mb-2'>
              <Badge bg={variant}>{report.count} emoji</Badge>
              {report.inline > 0 && <Badge bg='warning'>{report.inline} mid-sentence</Badge>}
            </div>
            <p className='small mb-0'>{verdict}</p>
          </div>
        </Col>
      </Row>

      {/* ── the dictionary, so the page has something to browse ── */}
      <hr className='my-4' />
      <h3 className='h6 fw-bold mb-2'>What this translator knows</h3>
      <p className='text-muted small'>
        {EMOJI_LIST.length} emoji, chosen for being unambiguous. Click one to add it to your text.
      </p>
      <div className='d-flex flex-wrap gap-1'>
        {EMOJI_LIST.map(({ char, name }) => (
          <button key={char + name} type='button' className='btn btn-sm btn-light border'
                  title={name} aria-label={`Add ${name}`}
                  onClick={() => setInput(t => `${t}${t.endsWith(' ') || !t ? '' : ' '}${char}`)}>
            <span style={{ fontSize: '1.1rem' }}>{char}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
